/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  BatchedEventAction,
  EventAction,
  EventActionBatch,
  KeyValue,
  PlanAction,
} from "~/generated/graphql";
import { isNil } from "~/tools";
import {
  EventEntity,
  FlatEventEntity,
  PlanEntityIncomingEventDetails,
} from "~/views/events/types";
import { paramsExcludedFromBaselinePatching } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/constant";
import {
  FormField,
  PlanEntity,
  SelectedPlanEntity,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";

type EntityWithInputParams =
  | {
      inputParameters?: SelectedPlanEntity[FormField.INPUT_PARAMETERS] | null;
    }
  | undefined;

export const isPatchOrScanEntity = (
  entity?:
    | SelectedPlanEntity
    | PlanEntity
    | EventEntity
    | PlanEntityIncomingEventDetails
) => {
  if (isNil(entity)) {
    return false;
  }

  const entityName = entity.action?.name ?? entity?.actionName ?? entity.name;

  return entityName === "PATCH" || entityName === "SCAN";
};

export const isBaselinePatchingEnabled = (
  entities: SelectedPlanEntity[] | FlatEventEntity[]
) => {
  return entities.some(
    (entity) =>
      isBaselineSourceEntity(entity) ||
      (("batchActions" in entity &&
        entity?.batchActions?.some(isBaselineSourceEntity)) ??
        false)
  );
};

const isBaselineSourceParam = (param: KeyValue) =>
  param.key === "BaselineSource" &&
  (param.value === "true" || param.value === "1");

export const isBaselineSourceEntity = <T extends EntityWithInputParams>(
  entity: T
) => {
  return !!entity?.inputParameters?.some(isBaselineSourceParam);
};

export const isBaselineDependentEntity = (
  entity?: SelectedPlanEntity | EventEntity | PlanEntityIncomingEventDetails,
  entities?: SelectedPlanEntity[] | FlatEventEntity[]
) => {
  if (
    isNil(entity) ||
    !isPatchOrScanEntity(entity) ||
    isBaselineSourceEntity(entity) ||
    isNil(entities) ||
    !isBelowBaselineSourceEntity(entity, entities)
  ) {
    return false;
  }

  return !!entities?.some((item) => {
    if (isBaselineSourceEntity(item)) {
      if ("listId" in entity && "listId" in item) {
        return item.listId !== entity.listId;
      }
      return item.id !== entity.id;
    }
    return false;
  });
};

export const isBelowBaselineSourceEntity = (
  entity?: SelectedPlanEntity | EventEntity | PlanEntityIncomingEventDetails,
  entities?: SelectedPlanEntity[] | FlatEventEntity[]
) => {
  if (isNil(entity) || isNil(entities)) {
    return false;
  }

  const entityIndex = entities.findIndex((item) => {
    if ("listId" in entity && "listId" in item) {
      return item.listId === entity.listId;
    }

    return item.id === entity.id;
  });

  return entityIndex > getBaselineSourceIndex(entities);
};

export const getBaselineSourceIndex = (
  entities: SelectedPlanEntity[] | PlanAction[] | FlatEventEntity[]
) => {
  return entities.findIndex((entity) =>
    entity.inputParameters?.some(isBaselineSourceParam)
  );
};

export const updateBaselineDependentEntities = (
  entities: SelectedPlanEntity[],
  baselineSource?: SelectedPlanEntity,
  enablingBaselinePatching?: boolean
) => {
  if (
    isNil(baselineSource) ||
    (!isBaselineSourceEntity(baselineSource) && !enablingBaselinePatching)
  ) {
    return entities;
  }

  const allEntities = entities.reduce<SelectedPlanEntity[]>((acc, entity) => {
    if (entity.batchActions?.length) {
      return [...acc, entity, ...entity.batchActions];
    }
    return [...acc, entity];
  }, []);

  const updateEntityIfDependent = (
    entity: SelectedPlanEntity
  ): SelectedPlanEntity => {
    if (isBaselineDependentEntity(entity, allEntities)) {
      return {
        ...entity,
        inputParameters: getParamsForDependentEntity(
          entity,
          baselineSource.inputParameters
        ),
      };
    }
    return entity;
  };

  return entities.map((entity) => {
    const updatedEntity = updateEntityIfDependent(entity);

    if (updatedEntity.batchActions?.length) {
      return {
        ...updatedEntity,
        batchActions: updatedEntity.batchActions.map(updateEntityIfDependent),
      };
    }

    return updatedEntity;
  });
};

export const getParamsForDependentEntity = (
  targetEntity: SelectedPlanEntity,
  sourceInputParameters: SelectedPlanEntity[FormField.INPUT_PARAMETERS]
): SelectedPlanEntity[FormField.INPUT_PARAMETERS] => {
  const sourceParamsMap = new Map(
    sourceInputParameters?.map((param) => [param.key, param.value]) || []
  );
  const targetParamsMap = new Map(
    targetEntity.inputParameters?.map((param) => [param.key, param.value]) || []
  );
  const excludedParamsSet = new Set(paramsExcludedFromBaselinePatching);

  return (targetEntity.parameterDefinitions || [])
    .map((paramDef) => {
      const key = paramDef.name;
      const value = (() => {
        if (key === "BaselineSource") {
          return "false";
        }
        if (excludedParamsSet.has(key)) {
          return targetParamsMap.get(key) ?? paramDef.defaultValue ?? "";
        }
        if (sourceParamsMap.has(key)) {
          return sourceParamsMap.get(key);
        }

        return null;
      })();

      if (value === null) {
        return null;
      }

      return { key, value };
    })
    .filter((param): param is KeyValue => param !== null);
};

export const baselineTagToShow = (
  entity:
    | SelectedPlanEntity
    | PlanEntity
    | EventEntity
    | PlanEntityIncomingEventDetails,
  entities: SelectedPlanEntity[] | FlatEventEntity[]
) => {
  if (!isPatchOrScanEntity(entity) || !isBaselinePatchingEnabled(entities)) {
    return;
  }

  const isEntityBaselineSource = isBaselineSourceEntity(entity);
  const isEntityBaselineDependent = isBaselineDependentEntity(entity, entities);

  if (isEntityBaselineSource && !isEntityBaselineDependent) {
    return "source";
  }

  if (!isEntityBaselineSource && isEntityBaselineDependent) {
    return "dependent";
  }
};

export const getFlatPlanEntities = (entities: SelectedPlanEntity[]) => {
  return entities.flatMap((entity) => {
    if ("batchActions" in entity && entity.batchActions) {
      return entity.batchActions;
    }

    return [entity];
  });
};

type BatchActionItems<T> = T extends { batchActions?: (infer U)[] } ? U : never;

type FlattenedEventEntity<T> =
  | Exclude<T, EventActionBatch>
  | (T extends EventActionBatch ? BatchedEventAction : never)
  | BatchActionItems<T>;

export const getFlatEventEntities = <
  T extends EventAction | EventActionBatch | PlanEntityIncomingEventDetails
>(
  entities: T[]
): FlattenedEventEntity<T>[] => {
  return entities.flatMap((entity) => {
    if ("actions" in entity) {
      return entity.actions as FlattenedEventEntity<T>[];
    }

    if ("batchActions" in entity && entity.batchActions) {
      return entity.batchActions as FlattenedEventEntity<T>[];
    }

    return [entity as FlattenedEventEntity<T>];
  });
};

export const updateBaselineSourceParameter = <T extends EntityWithInputParams>(
  entity: T,
  value: "true" | "false" = "false"
): T => {
  if (isBaselineSourceEntity(entity)) {
    const updatedInputParameters = entity?.inputParameters?.map((param) =>
      param.key === "BaselineSource" ? { ...param, value } : param
    );
    return { ...entity, inputParameters: updatedInputParameters };
  }
  return entity;
};
